// src/components/messaging/GlobalMessagingModals.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useMessaging } from '../../contexts/MessagingContext.tsx';
import { MessagingModal } from './MessagingModal.tsx';
import { ChatModal } from './ChatModal.tsx';

interface GlobalMessagingModalsProps {
  onViewUser?: (userId: string) => void; // Add this prop
}

export const GlobalMessagingModals: React.FC<GlobalMessagingModalsProps> = ({ onViewUser }) => {
  const { user } = useAuth();
  const {
    showMessagingModal,
    showChatModal,
    conversationId,
    targetUser,
    openChatWithConversation,
    closeAllModals,
  } = useMessaging();

  const [actualConversationId, setActualConversationId] = useState<string | null>(null);

  // Handle starting conversation with a user
  useEffect(() => {
    if (showChatModal && targetUser && !conversationId) {
      startOrContinueConversation();
    } else if (showChatModal && conversationId) {
      setActualConversationId(conversationId);
    }
  }, [showChatModal, targetUser, conversationId]);

  const startOrContinueConversation = async () => {
    if (!targetUser || !user) return;

    try {
      const token = localStorage.getItem('authToken');
      
      // First, try to find existing conversation
      const conversationsResponse = await fetch('https://aztrainersbackend.onrender.com/api/conversations/', {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (conversationsResponse.ok) {
        const data = await conversationsResponse.json();
        const existingConversation = data.conversations.find((conv: any) => 
          conv.participants.some((p: any) => p.id === targetUser.id)
        );

        if (existingConversation) {
          // Continue existing conversation
          setActualConversationId(existingConversation.conversation_id);
        } else {
          // Start new conversation
          const startResponse = await fetch('https://aztrainersbackend.onrender.com/api/conversations/start/', {
            method: 'POST',
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_id: targetUser.id
            })
          });

          if (startResponse.ok) {
            const startData = await startResponse.json();
            setActualConversationId(startData.conversation_id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  };

  const handleConversationSelect = (convId: string) => {
    openChatWithConversation(convId);
  };

  const handleModalClose = () => {
    setActualConversationId(null);
    closeAllModals();
  };

  return (
    <>
      {/* Messaging Modal (conversation list) */}
      <MessagingModal
        isOpen={showMessagingModal}
        onClose={closeAllModals}
        onConversationSelect={handleConversationSelect}
      />

      {/* Chat Modal (individual conversation) */}
      <ChatModal
        isOpen={showChatModal}
        onClose={handleModalClose}
        conversationId={actualConversationId}
        targetUser={targetUser || undefined}
        onViewUser={onViewUser} // Pass the navigation function
      />
    </>
  );
};